import React, { useEffect, useState } from 'react'
import { Card, Form, Button, Row, Col, Spin, Table } from 'antd'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import BaseListing from 'src/BaseListings'
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector'
import { VOUCHER_STATUS_DROPDOWN_OPTIONS } from 'src/models/vouchers'
import WidgetProjectSelector from 'src/widgets/Selectors/WidgetProjectSelector'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import WidgetDatePicker from 'src/widgets/DatePickers/WidgetDatePicker'
import { TYPE_GROUP, TYPE_TOTAL } from 'src/models/pnlConfig'
import { CaretDownFilled, CaretRightFilled, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons'
import WidgetBranchMultiSelector from 'src/widgets/Multi-Selectors/WidgetBranchMultiSelector'
import '../GrossProfitSheet/GrossProfitSheet.css'
import Pdf from './Pdf'

const SUB_BRANCH_OPTIONS = [
  { value: 0, label: 'No' },
  { value: 1, label: 'Yes' },
]
const CHANGE_SUB_BRANCH_PERMISSION = 'Change GP Report Sub Branch'

const BRANCH_COLUMN_COLORS = [
  { headerText: '#0f3d75', cellText: '#1d4ed8' },
  { headerText: '#166534', cellText: '#15803d' },
  { headerText: '#9a3412', cellText: '#c2410c' },
  { headerText: '#5b21b6', cellText: '#7c3aed' },
  { headerText: '#991b1b', cellText: '#dc2626' },
  { headerText: '#155e75', cellText: '#0891b2' },
]

const GP_EXCEL_NUMBER_FORMAT = '#,##0;(#,##0);-'
const GP_EXCEL_HEADER_FILL = '#F5F7FB'
const GP_EXCEL_TOTAL_FILL = '#F3F4F6'
const GP_EXCEL_TEXT_COLOR = '#111827'

const escapeGpExcelXml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')

const normalizeGpExportNumber = (value) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return 0
  }

  if (value === '-') {
    return 0
  }

  const sanitizedValue = String(value).replace(/,/g, '').trim()
  const parsedValue = Number(sanitizedValue)

  return Number.isNaN(parsedValue) ? 0 : parsedValue
}

const getGpExcelBranchLayoutValue = (row = {}, branchLayout = {}) =>
  (branchLayout.columns || []).reduce(
    (sum, column) => sum + normalizeGpExportNumber(row?.[column.key]),
    0,
  )

const getGpExcelColumnWidths = (branches = [], isNotesSheet = false) => {
  const baseWidths = isNotesSheet ? [58, 14] : [64]
  const branchWidth = 28
  const totalWidth = 22

  return [
    ...baseWidths.map((width) => ({ wch: width })),
    ...branches.map(() => ({ wch: branchWidth })),
    { wch: totalWidth },
  ]
}

const buildGpExcelStylesXml = () => {
  let xml = '<Styles>'
  xml += `<Style ss:ID="sHeaderDescription"><Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:WrapText="1"/><Font ss:Bold="1" ss:Size="12" ss:Color="${GP_EXCEL_TEXT_COLOR.replace('#', '')}"/><Interior ss:Color="${GP_EXCEL_HEADER_FILL}" ss:Pattern="Solid"/></Style>`
  xml += `<Style ss:ID="sHeaderTotal"><Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/><Font ss:Bold="1" ss:Size="12" ss:Color="${GP_EXCEL_TEXT_COLOR.replace('#', '')}"/><Interior ss:Color="${GP_EXCEL_HEADER_FILL}" ss:Pattern="Solid"/></Style>`
  xml += `<Style ss:ID="sBodyDescription"><Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:WrapText="1"/><Font ss:Size="12" ss:Color="${GP_EXCEL_TEXT_COLOR.replace('#', '')}"/></Style>`
  xml += `<Style ss:ID="sBodyDescriptionBold"><Alignment ss:Horizontal="Left" ss:Vertical="Center" ss:WrapText="1"/><Font ss:Bold="1" ss:Size="12" ss:Color="${GP_EXCEL_TEXT_COLOR.replace('#', '')}"/></Style>`
  xml += `<Style ss:ID="sBodyTotal"><Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:WrapText="1"/><Font ss:Size="12" ss:Color="${GP_EXCEL_TEXT_COLOR.replace('#', '')}"/><NumberFormat ss:Format="${GP_EXCEL_NUMBER_FORMAT}"/><Interior ss:Color="${GP_EXCEL_TOTAL_FILL}" ss:Pattern="Solid"/></Style>`
  xml += `<Style ss:ID="sBodyTotalBold"><Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:WrapText="1"/><Font ss:Bold="1" ss:Size="12" ss:Color="${GP_EXCEL_TEXT_COLOR.replace('#', '')}"/><NumberFormat ss:Format="${GP_EXCEL_NUMBER_FORMAT}"/><Interior ss:Color="${GP_EXCEL_TOTAL_FILL}" ss:Pattern="Solid"/></Style>`

  BRANCH_COLUMN_COLORS.forEach((color, index) => {
    const headerColor = color.headerText.replace('#', '')
    const bodyColor = color.cellText.replace('#', '')

    xml += `<Style ss:ID="sHeaderBranch${index}"><Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:WrapText="1"/><Font ss:Bold="1" ss:Size="12" ss:Color="${headerColor}"/><Interior ss:Color="${GP_EXCEL_HEADER_FILL}" ss:Pattern="Solid"/></Style>`
    xml += `<Style ss:ID="sBodyBranch${index}"><Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:WrapText="1"/><Font ss:Size="12" ss:Color="${bodyColor}"/><NumberFormat ss:Format="${GP_EXCEL_NUMBER_FORMAT}"/></Style>`
    xml += `<Style ss:ID="sBodyBranch${index}Bold"><Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:WrapText="1"/><Font ss:Bold="1" ss:Size="12" ss:Color="${bodyColor}"/><NumberFormat ss:Format="${GP_EXCEL_NUMBER_FORMAT}"/></Style>`
  })

  xml += '</Styles>'

  return xml
}

const getGpExcelStyleId = ({ columnType, branchIndex = null, bold = false }) => {
  if (columnType === 'total') {
    return bold ? 'sBodyTotalBold' : 'sBodyTotal'
  }

  if (columnType === 'branch' && branchIndex !== null) {
    const normalizedBranchIndex = branchIndex % BRANCH_COLUMN_COLORS.length
    return bold ? `sBodyBranch${normalizedBranchIndex}Bold` : `sBodyBranch${normalizedBranchIndex}`
  }

  if (bold) {
    return 'sBodyDescriptionBold'
  }

  return 'sBodyDescription'
}

const renderGpExcelCell = (value, styleId, type = 'String') =>
  `<Cell ss:StyleID="${styleId}"><Data ss:Type="${type}">${escapeGpExcelXml(value)}</Data></Cell>`

const renderGpExcelRow = (cells = []) => `<Row>${cells.join('')}</Row>`

function getGpVisibleRows(rows = []) {
  return (rows || []).filter((row) => isRowVisible(row))
}

const buildGpExcelSummarySheetXml = (rows = [], branches = []) => {
  const columnXml = getGpExcelColumnWidths(branches, false)
    .map(({ wch }) => `<Column ss:Width="${wch}"/>`)
    .join('')

  const headerRow = [
    renderGpExcelCell('Account Description', 'sHeaderDescription'),
    ...branches.map((branch, index) =>
      renderGpExcelCell(
        branch.label || `Branch ${branch.value}`,
        `sHeaderBranch${index % BRANCH_COLUMN_COLORS.length}`,
      ),
    ),
    renderGpExcelCell('Total', 'sHeaderTotal'),
  ]

  const dataRows = getGpVisibleRows(rows).map((row) => {
    const boldRow = row.type === TYPE_TOTAL || row.type === TYPE_GROUP || isLabelRow(row)
    const cells = [renderGpExcelCell(row.name || '', getGpExcelStyleId({ columnType: 'description', bold: boldRow }))]

    branches.forEach((branch, index) => {
      const key = `current_year_balance_${branch.value}`
      const value = row[key]

      if (isPercentageRow(row)) {
        cells.push(
          renderGpExcelCell(
            `${Number(value || 0).toFixed(2)} %`,
            getGpExcelStyleId({ columnType: 'branch', branchIndex: index, bold: boldRow }),
          ),
        )
        return
      }

      cells.push(
        renderGpExcelCell(
          normalizeGpExportNumber(value),
          getGpExcelStyleId({ columnType: 'branch', branchIndex: index, bold: boldRow }),
          'Number',
        ),
      )
    })

    if (isPercentageRow(row)) {
      cells.push(
        renderGpExcelCell(
          `${Number(row.total || 0).toFixed(2)} %`,
          getGpExcelStyleId({ columnType: 'total', bold: boldRow }),
        ),
      )
      return renderGpExcelRow(cells)
    }

    cells.push(
      renderGpExcelCell(
        normalizeGpExportNumber(row.total),
        getGpExcelStyleId({ columnType: 'total', bold: boldRow }),
        'Number',
      ),
    )

    return renderGpExcelRow(cells)
  })

  return `<Worksheet ss:Name="GP Report"><Table>${columnXml}${renderGpExcelRow(headerRow)}${dataRows.join('')}</Table></Worksheet>`
}

const buildGpExcelNotesSheetXml = (rows = [], branchLayouts = []) => {
  const columnXml = getGpExcelColumnWidths(branchLayouts, true)
    .map(({ wch }) => `<Column ss:Width="${wch}"/>`)
    .join('')

  const headerRow = [
    renderGpExcelCell('Account Description', 'sHeaderDescription'),
    renderGpExcelCell('Note', 'sHeaderDescription'),
    ...branchLayouts.map((branch, index) =>
      renderGpExcelCell(
        branch.branch_label || `Branch ${branch.branch_id}`,
        `sHeaderBranch${index % BRANCH_COLUMN_COLORS.length}`,
      ),
    ),
    renderGpExcelCell('Total', 'sHeaderTotal'),
  ]

  const sheetRows = []

  const addStructuralRows = (row, note, level = 0) => {
    const labelRow = [
      renderGpExcelCell(`${note}  ${row.name || ''}`, 'sBodyDescriptionBold'),
      renderGpExcelCell(note, 'sBodyDescriptionBold'),
      ...branchLayouts.map((_, index) => renderGpExcelCell('', getGpExcelStyleId({ columnType: 'branch', branchIndex: index, bold: true }))),
      renderGpExcelCell('', 'sBodyTotalBold'),
    ]

    sheetRows.push(renderGpExcelRow(labelRow))

    const visibleChildren = getGpVisibleRows(row.children || [])
    let childCounter = 0

    visibleChildren.forEach((child) => {
      const childNote = `${note}.${++childCounter}`

      if (child.type === TYPE_GROUP || isLabelRow(child)) {
        addStructuralRows(child, childNote, level + 1)
        return
      }

      const boldRow = child.type === TYPE_TOTAL || isLabelRow(child)
      const childRow = [
        renderGpExcelCell(`${'    '.repeat(level + 1)}${child.name || ''}`, getGpExcelStyleId({ columnType: 'description', bold: boldRow })),
        renderGpExcelCell(childNote, getGpExcelStyleId({ columnType: 'description', bold: boldRow })),
      ]

      branchLayouts.forEach((branchLayout, index) => {
        const value = getGpExcelBranchLayoutValue(child, branchLayout)

        if (isPercentageRow(child)) {
          childRow.push(
            renderGpExcelCell(
              `${Number(value || 0).toFixed(2)} %`,
              getGpExcelStyleId({ columnType: 'branch', branchIndex: index, bold: boldRow }),
            ),
          )
          return
        }

        childRow.push(
          renderGpExcelCell(
            normalizeGpExportNumber(value),
            getGpExcelStyleId({ columnType: 'branch', branchIndex: index, bold: boldRow }),
            'Number',
          ),
        )
      })

      if (isPercentageRow(child)) {
        childRow.push(
          renderGpExcelCell(
            `${Number(child.total || 0).toFixed(2)} %`,
            getGpExcelStyleId({ columnType: 'total', bold: boldRow }),
          ),
        )
      } else {
        childRow.push(
          renderGpExcelCell(
            normalizeGpExportNumber(child.total),
            getGpExcelStyleId({ columnType: 'total', bold: boldRow }),
            'Number',
          ),
        )
      }

      sheetRows.push(renderGpExcelRow(childRow))
    })

    const subtotalRow = [
      renderGpExcelCell('', 'sBodyDescriptionBold'),
      renderGpExcelCell('', 'sBodyDescriptionBold'),
    ]

      branchLayouts.forEach((branchLayout, index) => {
        const value = getGpExcelBranchLayoutValue(row, branchLayout)

        if (isPercentageRow(row)) {
          subtotalRow.push(
          renderGpExcelCell(
            `${Number(value || 0).toFixed(2)} %`,
            getGpExcelStyleId({ columnType: 'branch', branchIndex: index, bold: true }),
          ),
        )
        return
      }

      subtotalRow.push(
        renderGpExcelCell(
          normalizeGpExportNumber(value),
          getGpExcelStyleId({ columnType: 'branch', branchIndex: index, bold: true }),
          'Number',
        ),
      )
    })

    if (isPercentageRow(row)) {
      subtotalRow.push(renderGpExcelCell(`${Number(row.total || 0).toFixed(2)} %`, 'sBodyTotalBold'))
    } else {
      subtotalRow.push(renderGpExcelCell(normalizeGpExportNumber(row.total), 'sBodyTotalBold', 'Number'))
    }

    sheetRows.push(renderGpExcelRow(subtotalRow))
  }

  let topLevelGroupCounter = 0
  getGpVisibleRows(rows).forEach((row) => {
    if (row.type === TYPE_GROUP || isLabelRow(row)) {
      addStructuralRows(row, String(++topLevelGroupCounter))
      return
    }

    const boldRow = row.type === TYPE_TOTAL || isLabelRow(row)
    const rowCells = [
      renderGpExcelCell(row.name || '', getGpExcelStyleId({ columnType: 'description', bold: boldRow })),
      renderGpExcelCell('', getGpExcelStyleId({ columnType: 'description', bold: boldRow })),
    ]

    branchLayouts.forEach((branchLayout, index) => {
      const value = getGpExcelBranchLayoutValue(row, branchLayout)

      if (isPercentageRow(row)) {
        rowCells.push(
          renderGpExcelCell(
            `${Number(value || 0).toFixed(2)} %`,
            getGpExcelStyleId({ columnType: 'branch', branchIndex: index, bold: boldRow }),
          ),
        )
        return
      }

      rowCells.push(
        renderGpExcelCell(
          normalizeGpExportNumber(value),
          getGpExcelStyleId({ columnType: 'branch', branchIndex: index, bold: boldRow }),
          'Number',
        ),
      )
    })

    if (isPercentageRow(row)) {
      rowCells.push(
        renderGpExcelCell(
          `${Number(row.total || 0).toFixed(2)} %`,
          getGpExcelStyleId({ columnType: 'total', bold: boldRow }),
        ),
      )
    } else {
      rowCells.push(
        renderGpExcelCell(
          normalizeGpExportNumber(row.total),
          getGpExcelStyleId({ columnType: 'total', bold: boldRow }),
          'Number',
        ),
      )
    }

    sheetRows.push(renderGpExcelRow(rowCells))
  })

  return `<Worksheet ss:Name="GP Notes"><Table>${columnXml}${renderGpExcelRow(headerRow)}${sheetRows.join('')}</Table></Worksheet>`
}

const buildGpExcelWorkbookXml = (rows = [], branchLayouts = []) => {
  const summaryBranches = flattenGpBranchLayouts(branchLayouts)
  const notesSheetXml = buildGpExcelNotesSheetXml(rows, branchLayouts)
  const summarySheetXml = buildGpExcelSummarySheetXml(rows, summaryBranches)

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
xmlns:o="urn:schemas-microsoft-com:office:office"
xmlns:x="urn:schemas-microsoft-com:office:excel"
xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
${buildGpExcelStylesXml()}
${summarySheetXml}
${notesSheetXml}
</Workbook>`
}

const isRowVisible = (row) => {
  const hasIsDisplay = Object.prototype.hasOwnProperty.call(row, 'is_display')
  const hasIsShow = Object.prototype.hasOwnProperty.call(row, 'is_show')

  const isDisplayVisible = !hasIsDisplay || row.is_display === true || row.is_display === 1 || row.is_display === '1'
  const isShowVisible = !hasIsShow || row.is_show === true || row.is_show === 1 || row.is_show === '1'

  return isDisplayVisible && isShowVisible
}

const normalizeGpReportRows = (rows = []) => {
  return rows
    .filter((row) => isRowVisible(row))
    .map((row) => {
      const normalizedChildren = Array.isArray(row.children) ? normalizeGpReportRows(row.children) : []

      return {
        ...row,
        name: row.label && String(row.label).trim() !== '' ? row.label : row.name,
        total: getRowTotal(row),
        children: normalizedChildren,
        hasItems: normalizedChildren.length > 0,
      }
    })
}

const isPercentageRow = (row) => row?.is_percentage === true || row?.is_percentage === 1 || row?.is_percentage === '1'

const isLabelRow = (row) => row?.is_label === true || row?.is_label === 1 || row?.is_label === '1'

const getRowBranchValues = (row) =>
  Object.keys(row || {})
    .filter((key) => key.startsWith('current_year_balance_'))
    .map((key) => Number(row[key] || 0))

const getRowTotal = (row) => {
  if (row?.total !== undefined && row?.total !== null) {
    return Number(row.total) || 0
  }

  if (isPercentageRow(row)) {
    const leafValues = Object.keys(row || {})
      .filter((key) => /^current_year_balance_\d+_\d+$/.test(key))
      .map((key) => Number(row[key] || 0))

    if (leafValues.length > 0) {
      return leafValues.reduce((sum, value) => sum + value, 0) / leafValues.length
    }

    const branchValues = Object.keys(row || {})
      .filter((key) => /^current_year_balance_\d+$/.test(key))
      .map((key) => Number(row[key] || 0))

    if (branchValues.length === 0) {
      return 0
    }

    return branchValues.reduce((sum, value) => sum + value, 0) / branchValues.length
  }

  const branchValues = getRowBranchValues(row)
  return branchValues.reduce((sum, value) => sum + value, 0)
}

const formatGpReportValue = (value, row) => {
  if (isLabelRow(row)) {
    return ''
  }

  return formatGpDisplayNumber(value, row)
}

const formatGpDisplayNumber = (value, row) => {
  if (isPercentageRow(row)) {
    return `${Number(value || 0).toFixed(2)} %`
  }

  const numericValue = Number(value || 0)
  const absoluteFormattedValue = Math.abs(numericValue).toLocaleString('en-US', {
    maximumFractionDigits: 0,
  })

  if (numericValue < 0) {
    return `(${absoluteFormattedValue})`
  }

  return absoluteFormattedValue
}

const isBranchVisibleInGpReport = (branch) => {
  const visibility = branch?.is_visible

  return visibility === true || visibility === 1 || visibility === '1'
}

const normalizeBranchOption = (branch) => ({
  ...branch,
  value: branch?.value ?? branch?.id,
  label: branch?.label ?? branch?.name,
})

const buildDefaultGpBranchLayouts = (branches = []) =>
  (branches || []).map((branch) => {
    const normalizedBranch = normalizeBranchOption(branch)

    return {
      branch_id: normalizedBranch.value,
      branch_label: normalizedBranch.label,
      uses_sub_branch: 0,
      columns: [
        {
          key: `current_year_balance_${normalizedBranch.value}`,
          label: 'Balance ( .Rs. )',
        },
      ],
    }
  })

const flattenGpBranchLayouts = (branchLayouts = []) =>
  (branchLayouts || []).flatMap((branchLayout) => {
    const columnCount = branchLayout?.columns?.length || 0

    return (branchLayout?.columns || []).map((column) => ({
      ...column,
      branch_id: branchLayout.branch_id,
      branch_label: branchLayout.branch_label,
      full_label:
        columnCount > 1
          ? `${branchLayout.branch_label} - ${column.label}`
          : branchLayout.branch_label,
    }))
  })

const hasBranchVisibilityFlag = (branch) => Object.prototype.hasOwnProperty.call(branch || {}, 'is_visible')

const getVisibleGpBranches = (assignedBranches = [], visibleBranchRecords = []) => {
  const normalizedAssignedBranches = (assignedBranches || [])
    .map((branch) => normalizeBranchOption(branch))
    .filter((branch) => branch.value !== undefined && branch.value !== null)

  if (normalizedAssignedBranches.some((branch) => hasBranchVisibilityFlag(branch))) {
    return normalizedAssignedBranches.filter(isBranchVisibleInGpReport)
  }

  if ((visibleBranchRecords || []).length === 0) {
    return normalizedAssignedBranches
  }

  const visibleBranchIds = new Set(
    (visibleBranchRecords || [])
      .map((branch) => normalizeBranchOption(branch))
      .filter(isBranchVisibleInGpReport)
      .map((branch) => String(branch.value)),
  )

  return normalizedAssignedBranches.filter((branch) => visibleBranchIds.has(String(branch.value)))
}

const GpReport = ({
  reportTitle = 'GP Report',
  reportEntityName = 'gp_report',
  reportEndpoint = 'reports/gp_report',
}) => {
  const Base = new BaseListing(reportEntityName)
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()

  const [projects, setProjects] = useState([])
  const assignedBranches = wtGetFromLocalStorage('branches', [])
  const [visibleBranches, setVisibleBranches] = useState(() => getVisibleGpBranches(assignedBranches))
  const [branchesReady, setBranchesReady] = useState(false)

  const [projectId, setProjectId] = useState()
  const [projectName, setProjectName] = useState('')

  const [voucherStatus, setVoucherStatus] = useState()
  const [voucherStatusName, setVoucherStatusName] = useState('')
  const [selectedBranches, setSelectedBranches] = useState([])
  const [subBranch, setSubBranch] = useState(0)
  const [gpReportData, setGpReportData] = useState([])
  const [asOf, setAsOf] = useState('')
  const companyName = wtGetFromLocalStorage('company_name')
  const canChangeSubBranch = Base.hasRights(CHANGE_SUB_BRANCH_PERMISSION)

  const [boldRows, setBoldRows] = useState([])
  const [expandedRows, setExpandedRows] = useState([])
  const [branchLayouts, setBranchLayouts] = useState([])
  const [gpReportHeaderColumns, setGpReportHeaderColumns] = useState([])

  const sortBranchesByConfiguredOrder = (branchList) => {
    const branchOrderMap = new Map((visibleBranches || []).map((branch, index) => [String(branch.value), index]))

    return [...branchList].sort((a, b) => {
      const leftIndex = branchOrderMap.get(String(a.value))
      const rightIndex = branchOrderMap.get(String(b.value))

      if (typeof leftIndex === 'number' && typeof rightIndex === 'number') {
        return leftIndex - rightIndex
      }
      if (typeof leftIndex === 'number') {
        return -1
      }
      if (typeof rightIndex === 'number') {
        return 1
      }

      return String(a.label).localeCompare(String(b.label))
    })
  }

  const buildGpReportTableColumns = (layouts = []) => {
    const columns = []
    const flattenedColumns = flattenGpBranchLayouts(layouts)

    if (layouts.length > 0) {
      columns.push({
        title: <span style={{ fontSize: '0.8rem', marginLeft: '5%' }}>Account Description</span>,
        dataIndex: 'name',
        key: 'name',
        align: 'left',
        fixed: 'left',
        width: 420,
        onCell: (record) => ({
          onClick: () => handleExpand(record),
          style: { cursor: 'pointer' },
        }),
        render: (text, record) => (
          <span
            style={{
              fontWeight: record.type === TYPE_TOTAL || boldRows.includes(record.key) ? 'bold' : 'normal',
              fontSize: record.type === TYPE_TOTAL ? '0.83rem' : '0.8rem',
              cursor: 'pointer',
            }}
          >
            {text}
          </span>
        ),
      })

      let leafColumnIndex = 0

      layouts.forEach((branchLayout) => {
        columns.push({
          title: (
            <span style={{ fontSize: '0.8rem', fontWeight: 700 }}>
              {branchLayout.branch_label}
            </span>
          ),
          align: 'right',
          width: Math.max(160, (branchLayout.columns || []).length * 160),
          children: (branchLayout.columns || []).map((column) => {
            const branchColors = BRANCH_COLUMN_COLORS[leafColumnIndex % BRANCH_COLUMN_COLORS.length]
            const columnNode = {
              title: (
                <span style={{ fontSize: '0.8rem', color: branchColors.headerText }}>
                  {column.label}
                </span>
              ),
              dataIndex: column.key,
              key: column.key,
              align: 'right',
              width: 160,
              render: (text, record) => (
                <span
                  style={{
                    color: branchColors.cellText,
                    fontWeight: record.type === TYPE_TOTAL || boldRows.includes(record.key) ? 'bold' : 'normal',
                    fontSize: record.type === TYPE_TOTAL ? '0.78rem' : '0.72rem',
                  }}
                >
                  {formatGpReportValue(text, record)}
                </span>
              ),
            }

            leafColumnIndex++
            return columnNode
          }),
        })
      })

      columns.push({
        title: <span style={{ fontSize: '0.8rem', color: '#111827', fontWeight: 700 }}>Total</span>,
        align: 'right',
        width: 160,
        children: [
          {
            title: <span style={{ fontSize: '0.8rem', color: '#111827' }}>Balance ( .Rs. )</span>,
            dataIndex: 'total',
            key: 'total',
            align: 'right',
            width: 160,
            render: (text, record) => (
              <span
                style={{
                  fontWeight: record.type === TYPE_TOTAL || boldRows.includes(record.key) ? 'bold' : 'normal',
                  fontSize: record.type === TYPE_TOTAL ? '0.78rem' : '0.72rem',
                }}
              >
                {formatGpReportValue(text, record)}
              </span>
            ),
          },
        ],
      })
    }

    return {
      columns,
      leafColumnCount: flattenedColumns.length,
    }
  }

  const changeValues = (key, val) => {
    setGpReportData([])

    if (key === 'branch_id') {
      const sortedBranches = sortBranchesByConfiguredOrder(val)
      setSelectedBranches(sortedBranches)
      const nextBranchLayouts = buildDefaultGpBranchLayouts(sortedBranches)
      setBranchLayouts(nextBranchLayouts)
      setGpReportHeaderColumns(buildGpReportTableColumns(nextBranchLayouts).columns)
    }

    if (key === 'project_id') {
      setProjectId(val)
      if (val === '') {
        setProjectName('')
      }
    }

    if (key === 'voucher_status') {
      setVoucherStatus(val.value)
      setVoucherStatusName(val.label)
    }

    if (key === 'sub_branch') {
      if (!canChangeSubBranch) {
        setSubBranch(0)
        return
      }

      setSubBranch(val.value)
    }

    if (key === 'as_of') {
      setAsOf(val)
    }
  }

  useEffect(() => {
    Base.dropdownData('projects', {}, [setProjects], 1)
    wtApiCall(
      'dropdown',
      {
        dropdown_names: 'branches',
      },
      'get',
      (response) => {
        const branchRecords = response?.data?.[0]?.branches || []
        setVisibleBranches(getVisibleGpBranches(assignedBranches, branchRecords))
        setBranchesReady(true)
      },
      () => {
        setVisibleBranches(getVisibleGpBranches(assignedBranches))
        setBranchesReady(true)
      },
    )
  }, [])

  useEffect(() => {
    if (!branchesReady || visibleBranches.length <= 0 || selectedBranches.length > 0) {
      return
    }

    const defaultBranches = sortBranchesByConfiguredOrder(visibleBranches)
    const defaultBranchLayouts = buildDefaultGpBranchLayouts(defaultBranches)

    setSelectedBranches(defaultBranches)
    setBranchLayouts(defaultBranchLayouts)
    setGpReportHeaderColumns(buildGpReportTableColumns(defaultBranchLayouts).columns)
  }, [branchesReady, visibleBranches, selectedBranches.length])

  const getReportData = () => {
    setIsLoading(true)
    wtApiCall(
      reportEndpoint,
      {
        as_of: asOf,
        branch_id: selectedBranches,
        project_id: projectId,
        voucher_status: voucherStatus,
        sub_branch: subBranch,
        version: 2,
      },
        'get',
      (response) => {
        if (response !== null && response.data !== null) {
          const responseBranchLayouts =
            Array.isArray(response.data.branch_layouts) && response.data.branch_layouts.length > 0
              ? response.data.branch_layouts
              : buildDefaultGpBranchLayouts(selectedBranches)

          setBranchLayouts(responseBranchLayouts)
          setGpReportHeaderColumns(buildGpReportTableColumns(responseBranchLayouts).columns)

          if (response.data.hasOwnProperty('gp_report_data')) {
            setGpReportData(normalizeGpReportRows(response.data.gp_report_data || []))
          } else {
            setGpReportData([])
          }
        }

        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const expandIcon = ({ expanded, onExpand, record }) => {
    if (record.hasItems && record.type === TYPE_GROUP) {
      return (
        <span style={{ marginRight: '2%' }}>
          {expanded ? (
            <CaretDownFilled style={{ color: '#706f71', fontSize: 12 }} onClick={(e) => onExpand(record, e)} />
          ) : (
            <CaretRightFilled style={{ color: '#706f71', fontSize: 12 }} onClick={(e) => onExpand(record, e)} />
          )}
        </span>
      )
    }

    return <span style={{ marginRight: '3%' }}></span>
  }

  const handleExpand = (record) => {
    const key = record.key
    if (record.type === TYPE_GROUP && record.hasItems) {
      setExpandedRows((prevExpandedRows) => {
        const isExpanded = prevExpandedRows.includes(key)
        return isExpanded
          ? prevExpandedRows.filter((rowKey) => rowKey !== key)
          : [...prevExpandedRows, key]
      })

      setBoldRows((prevBoldRows) => {
        const isBold = prevBoldRows.includes(key)
        return isBold ? prevBoldRows.filter((rowKey) => rowKey !== key) : [...prevBoldRows, key]
      })
    }
  }

  const exportToPdf = async () => {
    setIsLoading(true)
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      Pdf(gpReportData, asOf, branchLayouts)
    } catch (error) {
      console.error('Failed to generate PDF', error)
    }
    setIsLoading(false)
  }

  const exportToExcel = async () => {
    if (gpReportData.length <= 0) {
      return
    }

    setIsLoading(true)

    try {
      await wtApiPostWithContentTypeHandling('reports/gp_report/export', {
        as_of: asOf,
        branch_id: selectedBranches,
        project_id: projectId,
        voucher_status: voucherStatus,
        sub_branch: subBranch,
        version: 2,
      })
    } catch (error) {
      console.error('Failed to generate Excel export', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      <Form
        layout={'vertical'}
        form={form}
        onFinish={getReportData}
        style={{ maxWidth: '100%', margin: '0 auto' }}
      >
        <Card
          title={<span style={{ fontSize: '22px', marginLeft: '40%' }}>{companyName + ' ' + reportTitle}</span>}
          style={{ width: '100%' }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={15} sm={10} md={4}>
              <label>As Of: *</label>
              <WidgetDatePicker value={asOf} setDate={setAsOf} placeholder="As Of" />
            </Col>
            <Col xs={18} sm={12} md={11}>
              <label className="branch-multi-select">Branch: *</label>
              <WidgetBranchMultiSelector
                Branches={visibleBranches}
                onChange={(values) => changeValues('branch_id', values)}
                value={selectedBranches}
              />
            </Col>
            <Col xs={12} sm={8} md={3}>
              <label>Voucher Status:</label>
              <WidgetSelector
                selectedValue={voucherStatus}
                displayValue={voucherStatusName}
                options={VOUCHER_STATUS_DROPDOWN_OPTIONS}
                showCloudIcon={false}
                size={'md'}
                onChange={(values) => changeValues('voucher_status', values)}
              />
            </Col>
            <Col xs={12} sm={8} md={3}>
              <label>Sub Branch:</label>
              <WidgetSelector
                selectedValue={subBranch}
                displayValue={subBranch ? 'Yes' : 'No'}
                options={SUB_BRANCH_OPTIONS}
                showCloudIcon={false}
                size={'md'}
                disabled={!canChangeSubBranch}
                onChange={(values) => changeValues('sub_branch', values)}
              />
            </Col>
            <Col xs={18} sm={12} md={4}>
              <label>Project:</label>
              <WidgetProjectSelector
                onChange={(value) => changeValues('project_id', value)}
                data={projects}
                value={projectId}
                displayValue={projectName}
                height={35}
                reloadDropdownData={() => Base.dropdownData('projects', {}, [setProjects], 1)}
              />
            </Col>
            <Col xs={8} sm={4} md={2}>
              <Button style={{ marginTop: 35 }} type="primary" htmlType="submit" disabled={isLoading}>
                Search
              </Button>
            </Col>
          </Row>
        </Card>

        <br />
        <br />
        <div style={{ overflow: 'auto', margin: 'auto', width: '100%' }}>
          {gpReportData.length > 0 && (
            <>
              <Button
                style={{ marginBottom: 10, float: 'right', backgroundColor: '#1d4ed8', color: '#fff', marginLeft: 8 }}
                onClick={exportToExcel}
                disabled={isLoading}
                icon={<FileExcelOutlined />}
              />
              <Button
                style={{ marginBottom: 10, float: 'right', backgroundColor: '#FF0000', color: '#fff' }}
                onClick={exportToPdf}
                disabled={isLoading}
                icon={<FilePdfOutlined />}
              />
            </>
          )}
          {gpReportHeaderColumns.length > 0 && (
            <Table
              columns={gpReportHeaderColumns}
              dataSource={gpReportData}
              pagination={false}
              expandable={{ expandedRowKeys: expandedRows, expandIcon: expandIcon }}
              scroll={{ x: flattenGpBranchLayouts(branchLayouts).length * 160 + 620 + 160, y: 600 }}
              bordered
              size="small"
              loading={{ indicator: <div><Spin /></div>, spinning: isLoading }}
            />
          )}
        </div>
      </Form>
    </div>
  )
}

export default GpReport
